import { makeAutoObservable, runInAction } from "mobx";

export interface DialogState {
    isOpen: boolean;
    dialogName: string;
    dialogTab?: string;
    dialogContext?: string;
    activeTabIndex: number;
}

/**
 * Manages dialog state and operations.
 * Follows Single Responsibility Principle by focusing only on dialog management.
 */
export class DialogManager {
    // Observable state
    private dialogState: DialogState = {
        isOpen: false,
        dialogName: "",
        dialogTab: undefined,
        dialogContext: undefined,
        activeTabIndex: 0
    };

    forceFullScreenDialog = false;

    constructor() {
        makeAutoObservable(this);
    }

    /**
     * Opens a dialog with specified configuration
     * @param dialogName - Name/identifier of the dialog to open
     * @param dialogTab - Optional tab to focus within the dialog
     * @param dialogContext - Optional context data for the dialog
     */
    openDialog({
        dialogName,
        dialogTab,
        dialogContext,
    }: {
        dialogName: string;
        dialogTab?: string;
        dialogContext?: string;
    }): void {
        runInAction(() => {
            this.dialogState = {
                isOpen: true,
                dialogName,
                dialogTab,
                dialogContext,
                activeTabIndex: 0
            };
        });
    }

    /**
     * Closes the currently open dialog
     */
    closeDialog(): void {
        runInAction(() => {
            this.dialogState = {
                isOpen: false,
                dialogName: "",
                dialogTab: undefined,
                dialogContext: undefined,
                activeTabIndex: 0
            };
        });
    }

    /**
     * Sets the active tab index within the current dialog
     * @param tabIndex - Zero-based index of the tab to activate
     */
    setActiveTab(tabIndex: number): void {
        if (this.dialogState.isOpen) {
            runInAction(() => {
                this.dialogState.activeTabIndex = tabIndex;
            });
        }
    }

    /**
     * Toggles the force full screen mode for dialogs
     */
    toggleForceFullScreenDialog(): void {
        runInAction(() => {
            this.forceFullScreenDialog = !this.forceFullScreenDialog;
        });
    }

    /**
     * Sets the force full screen mode for dialogs
     * @param force - Whether to force full screen mode
     */
    setForceFullScreenDialog(force: boolean): void {
        runInAction(() => {
            this.forceFullScreenDialog = force;
        });
    }

    /**
     * Checks if a dialog is currently open
     * @returns True if any dialog is open
     */
    get isDialogOpen(): boolean {
        return this.dialogState.isOpen;
    }

    /**
     * Gets the current dialog name
     * @returns Current dialog name or empty string if no dialog is open
     */
    get currentDialogName(): string {
        return this.dialogState.dialogName;
    }

    /**
     * Gets the current dialog tab
     * @returns Current dialog tab or undefined if no tab is specified
     */
    get currentDialogTab(): string | undefined {
        return this.dialogState.dialogTab;
    }

    /**
     * Gets the current dialog context
     * @returns Current dialog context or undefined if no context is set
     */
    get currentDialogContext(): string | undefined {
        return this.dialogState.dialogContext;
    }

    /**
     * Gets the current active tab index
     * @returns Zero-based index of the active tab
     */
    get activeTabIndex(): number {
        return this.dialogState.activeTabIndex;
    }

    /**
     * Determines if the dialog should be rendered in full screen mode
     * @returns True if dialog should be full screen
     */
    get shouldRenderFullScreen(): boolean {
        return this.forceFullScreenDialog;
    }

    /**
     * Determines if the dialog should be draggable
     * @returns True if dialog should be draggable (opposite of full screen)
     */
    get shouldEnableDraggable(): boolean {
        return !this.forceFullScreenDialog;
    }

    /**
     * Gets a copy of the current dialog state for debugging
     * @returns Copy of current dialog state
     */
    getDialogStateCopy(): DialogState {
        return { ...this.dialogState };
    }
}
